import React, { useState, useEffect } from 'react';
import { adminQuotesNewAPI, adminProductsAPI, paymentAPI } from '../../services/api';

export default function AdminQuotesNewPage() {
  const [quotes, setQuotes] = useState([]);
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [selectedQuote, setSelectedQuote] = useState(null);
  const [showPaymentModal, setShowPaymentModal] = useState(false);
  const [paymentQuote, setPaymentQuote] = useState(null);
  const [editingQuote, setEditingQuote] = useState(null); // Pour savoir si on édite ou crée
  const [paymentForm, setPaymentForm] = useState({
    amount: '',
    date: new Date().toISOString().split('T')[0],
    method: 'bank_transfer',
    reference: '',
    notes: ''
  });
  
  // Filtres
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  
  // Formulaire de création
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    client_address: '',
    projectType: 'Installation solaire',
    message: '',
    items: [{ product_id: '', description: '', quantity: 1, unit_price: 0, total_ht: 0 }],
    tva_rate: 20.0,
    discount_type: '',
    discount_value: 0,
    issue_date: new Date().toISOString().split('T')[0],
    notes: ''
  });

  useEffect(() => {
    loadQuotes();
    loadProducts();
  }, []);

  const loadQuotes = async () => {
    try {
      setLoading(true);
      setError(null);
      const data = await adminQuotesNewAPI.list();
      setQuotes(Array.isArray(data) ? data : []);
    } catch (err) {
      console.error('Erreur chargement devis:', err);
      setError('Erreur lors du chargement des devis');
    } finally {
      setLoading(false);
    }
  };

  const loadProducts = async () => {
    try {
      const data = await adminProductsAPI.getAll();
      setProducts(Array.isArray(data) ? data : []);
    } catch (err) {
      console.error('Erreur chargement produits:', err);
    }
  };

  const handleProductSelect = (index, productId) => {
    const newItems = [...formData.items];
    
    if (productId === 'manual') {
      newItems[index] = {
        product_id: '',
        description: '',
        quantity: 1,
        unit_price: 0,
        total_ht: 0
      };
    } else if (productId) {
      const product = products.find(p => p.id === productId);
      if (product) {
        const price = product.price === 'Sur devis' ? 0 : parseFloat(product.price) || 0;
        newItems[index] = {
          product_id: productId,
          description: product.name,
          quantity: 1,
          unit_price: price,
          total_ht: price
        };
      }
    }
    
    setFormData({ ...formData, items: newItems });
  };

  const handleItemChange = (index, field, value) => {
    const newItems = [...formData.items];
    newItems[index][field] = value;
    
    if (field === 'quantity' || field === 'unit_price') {
      newItems[index].total_ht = newItems[index].quantity * newItems[index].unit_price;
    }
    
    setFormData({ ...formData, items: newItems });
  };

  const addItem = () => {
    setFormData({
      ...formData,
      items: [...formData.items, { product_id: '', description: '', quantity: 1, unit_price: 0, total_ht: 0 }]
    });
  };

  const removeItem = (index) => {
    if (formData.items.length > 1) {
      const newItems = formData.items.filter((_, i) => i !== index);
      setFormData({ ...formData, items: newItems });
    }
  };

  const handleCreateQuote = async (e) => {
    e.preventDefault();
    try {
      const quoteData = {
        ...formData,
        // Convertir discount_type vide en null
        discount_type: formData.discount_type || null,
        // Convertir les dates vides en null
        issue_date: formData.issue_date || null,
        // Convertir les chaînes vides en null
        phone: formData.phone || null,
        client_address: formData.client_address || null,
        notes: formData.notes || null,
        items: formData.items.map(item => ({
          description: item.description,
          quantity: parseFloat(item.quantity) || 0,
          unit_price: parseFloat(item.unit_price) || 0,
          total_ht: parseFloat(item.total_ht) || 0
        }))
      };
      
      if (editingQuote) {
        // Mode édition
        await adminQuotesNewAPI.update(editingQuote.id, quoteData);
        alert('✅ Devis modifié avec succès!');
      } else {
        // Mode création
        await adminQuotesNewAPI.create(quoteData);
        alert('✅ Devis créé avec succès!');
      }
      
      setShowCreateModal(false);
      setEditingQuote(null);
      loadQuotes();
      resetForm();
    } catch (err) {
      console.error('Erreur devis:', err);
      const errorMessage = err.response?.data?.detail || err.message || 'Erreur inconnue';
      alert('❌ Erreur:\n\n' + errorMessage);
    }
  };

  const handleEditQuote = (quote) => {
    setEditingQuote(quote);
    setFormData({
      name: quote.name || '',
      email: quote.email || '',
      phone: quote.phone || '',
      client_address: quote.client_address || '',
      projectType: quote.projectType || 'Installation solaire',
      message: quote.message || '',
      items: quote.items && quote.items.length > 0 ? quote.items : [{ product_id: '', description: '', quantity: 1, unit_price: 0, total_ht: 0 }],
      tva_rate: quote.tva_rate || 20.0,
      discount_type: quote.discount_type || '',
      discount_value: quote.discount_value || 0,
      issue_date: quote.issue_date ? new Date(quote.issue_date).toISOString().split('T')[0] : new Date().toISOString().split('T')[0],
      notes: quote.notes || ''
    });
    setShowCreateModal(true);
  };

  const handleCancelQuote = async (quoteId) => {
    if (window.confirm('Êtes-vous sûr de vouloir annuler ce devis ?')) {
      try {
        await adminQuotesNewAPI.update(quoteId, { status: 'cancelled' });
        loadQuotes();
        alert('✅ Devis annulé');
      } catch (err) {
        console.error('Erreur annulation:', err);
        alert('Erreur lors de l\'annulation du devis');
      }
    }
  };

  const handleDelete = async (quoteId) => {
    if (window.confirm('Êtes-vous sûr de vouloir supprimer ce devis ?')) {
      try {
        await adminQuotesNewAPI.delete(quoteId);
        loadQuotes();
      } catch (err) {
        console.error('Erreur suppression:', err);
        alert('Erreur lors de la suppression du devis');
      }
    }
  };

  const handleOpenPaymentModal = (quote) => {
    setPaymentQuote(quote);
    const remainingAmount = (quote.total_ttc || 0) - (quote.amount_paid || 0);
    setPaymentForm({
      amount: remainingAmount > 0 ? remainingAmount.toString() : '',
      date: new Date().toISOString().split('T')[0],
      method: 'bank_transfer',
      reference: '',
      notes: ''
    });
    setShowPaymentModal(true);
  };

  const handleAddPayment = async (e) => {
    e.preventDefault();
    try {
      const paymentData = {
        amount: parseFloat(paymentForm.amount),
        date: paymentForm.date || null,
        method: paymentForm.method,
        reference: paymentForm.reference || null,
        notes: paymentForm.notes || null
      };

      await adminQuotesNewAPI.addPayment(paymentQuote.id, paymentData);
      setShowPaymentModal(false);
      loadQuotes();
      alert('✅ Acompte ajouté avec succès!');
    } catch (err) {
      console.error('Erreur ajout acompte:', err);
      const errorMessage = err.response?.data?.detail || err.message || 'Erreur inconnue';
      alert('❌ Erreur lors de l\'ajout de l\'acompte:\n\n' + errorMessage);
    }
  };

  const handleGeneratePayPalLink = async (quote) => {
    try {
      const remainingAmount = (quote.total_ttc || 0) - (quote.amount_paid || 0);
      
      if (remainingAmount <= 0) {
        alert('⚠️ Ce devis est déjà entièrement payé.');
        return;
      }

      const confirmed = window.confirm(`Générer un lien PayPal pour ${remainingAmount.toFixed(2)}€ (montant restant dû) ?`);
      if (!confirmed) return;

      const result = await paymentAPI.generateQuotePaymentLink(
        quote.id,
        remainingAmount,
        'EUR',
        `Devis ${quote.quote_number || quote.id.substring(0, 8)} - ${quote.name}`
      );

      if (result.success && result.payment_link) {
        // Copier le lien dans le presse-papiers
        navigator.clipboard.writeText(result.payment_link).then(() => {
          alert(`✅ Lien PayPal généré et copié!\n\n${result.payment_link}\n\nVous pouvez maintenant l'envoyer au client par email.`);
        }).catch(() => {
          alert(`✅ Lien PayPal généré:\n\n${result.payment_link}\n\nCopiez ce lien et envoyez-le au client.`);
        });
        
        // Recharger la liste des devis
        loadQuotes();
      } else {
        throw new Error('Erreur lors de la génération du lien');
      }
    } catch (err) {
      console.error('Erreur génération lien PayPal:', err);
      const errorMessage = err.response?.data?.detail || err.message || 'Erreur inconnue';
      alert('❌ Erreur lors de la génération du lien PayPal:\n\n' + errorMessage);
    }
  };

  const resetForm = () => {
    setFormData({
      name: '',
      email: '',
      phone: '',
      client_address: '',
      projectType: 'Installation solaire',
      message: '',
      items: [{ product_id: '', description: '', quantity: 1, unit_price: 0, total_ht: 0 }],
      tva_rate: 20.0,
      discount_type: '',
      discount_value: 0,
      issue_date: new Date().toISOString().split('T')[0],
      notes: ''
    });
  };

  const filteredQuotes = quotes.filter(quote => {
    const matchesSearch = 
      (quote.quote_number && quote.quote_number.toLowerCase().includes(searchTerm.toLowerCase())) ||
      quote.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      quote.email.toLowerCase().includes(searchTerm.toLowerCase());
    
    const matchesStatus = statusFilter === 'all' || quote.status === statusFilter;
    
    return matchesSearch && matchesStatus;
  });

  const formatDate = (dateStr) => {
    if (!dateStr) return '-';
    return new Date(dateStr).toLocaleDateString('fr-FR');
  };

  const formatPrice = (price) => {
    return new Intl.NumberFormat('fr-FR', { style: 'currency', currency: 'EUR' }).format(price);
  };

  const formatPriceFCFA = (priceEUR) => {
    const FCFA_RATE = 655.957;
    const priceFCFA = priceEUR * FCFA_RATE;
    return new Intl.NumberFormat('fr-FR', { style: 'currency', currency: 'XOF', minimumFractionDigits: 0 }).format(priceFCFA);
  };

  const getStatusBadge = (status) => {
    const badges = {
      pending: 'bg-yellow-100 text-yellow-800',
      in_progress: 'bg-blue-100 text-blue-800',
      completed: 'bg-green-100 text-green-800',
      cancelled: 'bg-red-100 text-red-800',
      paid: 'bg-emerald-100 text-emerald-800'
    };
    const labels = {
      pending: 'En attente',
      in_progress: 'En cours',
      completed: 'Terminé',
      cancelled: 'Annulé',
      paid: 'Payé'
    };
    return (
      <span className={`px-2 py-1 rounded text-xs font-semibold ${badges[status] || 'bg-gray-100 text-gray-800'}`}>
        {labels[status] || status}
      </span>
    );
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center py-20">
        <div className="text-lg">Chargement des devis...</div>
      </div>
    );
  }

  return (
    <div>
      <div className="flex items-center justify-between mb-6">
        <h2 className="text-2xl font-bold">Gestion des Devis</h2>
        <button
          onClick={() => setShowCreateModal(true)}
          className="px-4 py-2 bg-yellow-600 text-black rounded hover:bg-yellow-700"
        >
          Nouveau Devis
        </button>
      </div>

      {error && (
        <div className="mb-4 p-3 bg-red-100 text-red-800 rounded">
          {error}
        </div>
      )}

      {/* Filtres */}
      <div className="mb-6 flex gap-4">
        <input
          type="text"
          placeholder="Rechercher par numéro, nom ou email..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          className="flex-1 px-4 py-2 border rounded"
        />
        <select
          value={statusFilter}
          onChange={(e) => setStatusFilter(e.target.value)}
          className="px-4 py-2 border rounded"
        >
          <option value="all">Tous les statuts</option>
          <option value="pending">En attente</option>
          <option value="in_progress">En cours</option>
          <option value="completed">Terminés</option>
          <option value="cancelled">Annulés</option>
        </select>
      </div>

      {/* Liste des devis */}
      {filteredQuotes.length === 0 ? (
        <div className="text-center py-10 bg-gray-50 rounded">
          <p className="text-gray-500">Aucun devis trouvé</p>
        </div>
      ) : (
        <div className="bg-white rounded-lg shadow overflow-hidden">
          <table className="w-full">
            <thead className="bg-gray-50 border-b">
              <tr>
                <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase">Numéro</th>
                <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase">Client</th>
                <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase">Date</th>
                <th className="px-4 py-3 text-right text-xs font-semibold text-gray-600 uppercase">Total TTC (€)</th>
                <th className="px-4 py-3 text-right text-xs font-semibold text-gray-600 uppercase">Total TTC (FCFA)</th>
                <th className="px-4 py-3 text-center text-xs font-semibold text-gray-600 uppercase">Statut</th>
                <th className="px-4 py-3 text-center text-xs font-semibold text-gray-600 uppercase">Actions</th>
              </tr>
            </thead>
            <tbody>
              {filteredQuotes.map((quote) => (
                <tr key={quote.id} className="border-b hover:bg-gray-50">
                  <td className="px-4 py-3 font-mono text-sm">{quote.quote_number || quote.id.substring(0, 8)}</td>
                  <td className="px-4 py-3">
                    <div className="text-sm font-medium">{quote.name}</div>
                    <div className="text-xs text-gray-500">{quote.email}</div>
                  </td>
                  <td className="px-4 py-3 text-sm">{formatDate(quote.issue_date || quote.created_at)}</td>
                  <td className="px-4 py-3 text-right">
                    <div className="font-semibold">{formatPrice(quote.total_ttc || 0)}</div>
                    {quote.amount_paid > 0 && quote.status !== 'paid' && (
                      <div className="text-xs text-gray-500">
                        Payé: {formatPrice(quote.amount_paid)}<br/>
                        Restant: {formatPrice((quote.total_ttc || 0) - (quote.amount_paid || 0))}
                      </div>
                    )}
                  </td>
                  <td className="px-4 py-3 text-right">
                    <div className="font-semibold">{formatPriceFCFA(quote.total_ttc || 0)}</div>
                    {quote.amount_paid > 0 && quote.status !== 'paid' && (
                      <div className="text-xs text-gray-500">
                        Payé: {formatPriceFCFA(quote.amount_paid)}<br/>
                        Restant: {formatPriceFCFA((quote.total_ttc || 0) - (quote.amount_paid || 0))}
                      </div>
                    )}
                  </td>
                  <td className="px-4 py-3 text-center">{getStatusBadge(quote.status)}</td>
                  <td className="px-4 py-3">
                    <div className="flex justify-center gap-1 flex-wrap">
                      <button
                        onClick={() => adminQuotesNewAPI.downloadPDF(quote.id)}
                        className="px-2 py-1 bg-purple-600 text-white text-xs rounded hover:bg-purple-700"
                        title="Télécharger PDF"
                      >
                        📄 PDF
                      </button>
                      <button
                        onClick={() => setSelectedQuote(quote)}
                        className="px-2 py-1 bg-blue-600 text-white text-xs rounded hover:bg-blue-700"
                        title="Afficher les détails"
                      >
                        👁️ Afficher
                      </button>
                      {quote.status !== 'cancelled' && quote.status !== 'paid' && (
                        <button
                          onClick={() => handleEditQuote(quote)}
                          className="px-2 py-1 bg-yellow-600 text-black text-xs rounded hover:bg-yellow-700"
                          title="Modifier le devis"
                        >
                          ✏️ Modifier
                        </button>
                      )}
                      {quote.total_ttc > 0 && quote.status !== 'paid' && quote.status !== 'cancelled' && (
                        <button
                          onClick={() => handleOpenPaymentModal(quote)}
                          className="px-2 py-1 bg-green-600 text-white text-xs rounded hover:bg-green-700"
                          title="Ajouter un acompte"
                        >
                          💰 Acompte
                        </button>
                      )}
                      {quote.total_ttc > 0 && quote.status !== 'paid' && quote.status !== 'cancelled' && (
                        <button
                          onClick={() => handleGeneratePayPalLink(quote)}
                          className="px-2 py-1 bg-blue-500 text-white text-xs rounded hover:bg-blue-600"
                          title="Générer un lien de paiement PayPal"
                        >
                          💳 Lien PayPal
                        </button>
                      )}
                      {quote.status !== 'cancelled' && quote.status !== 'paid' && (
                        <button
                          onClick={() => handleCancelQuote(quote.id)}
                          className="px-2 py-1 bg-orange-600 text-white text-xs rounded hover:bg-orange-700"
                          title="Annuler le devis"
                        >
                          🚫 Annuler
                        </button>
                      )}
                      <button
                        onClick={() => handleDelete(quote.id)}
                        className="px-2 py-1 bg-red-600 text-white text-xs rounded hover:bg-red-700"
                        title="Supprimer définitivement"
                      >
                        🗑️ Supprimer
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Modal Détails */}
      {selectedQuote && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4" onClick={() => setSelectedQuote(null)}>
          <div className="bg-white rounded-lg p-6 max-w-2xl w-full max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
            <h3 className="text-xl font-bold mb-4">Devis {selectedQuote.quote_number || selectedQuote.id.substring(0, 8)}</h3>
            
            <div className="space-y-4">
              <div>
                <h4 className="font-semibold mb-2">Informations Client</h4>
                <p><strong>Nom:</strong> {selectedQuote.name}</p>
                <p><strong>Email:</strong> {selectedQuote.email}</p>
                <p><strong>Téléphone:</strong> {selectedQuote.phone || '-'}</p>
                <p><strong>Adresse:</strong> {selectedQuote.client_address || '-'}</p>
              </div>
              
              {selectedQuote.items && selectedQuote.items.length > 0 && (
                <>
                  <div>
                    <h4 className="font-semibold mb-2">Articles</h4>
                    <table className="w-full text-xs">
                      <thead className="bg-gray-50">
                        <tr>
                          <th className="px-2 py-1 text-left">Description</th>
                          <th className="px-2 py-1 text-right">Qté</th>
                          <th className="px-2 py-1 text-right">Prix HT (€)</th>
                          <th className="px-2 py-1 text-right">Prix HT (FCFA)</th>
                          <th className="px-2 py-1 text-right">Total HT (€)</th>
                          <th className="px-2 py-1 text-right">Total HT (FCFA)</th>
                        </tr>
                      </thead>
                      <tbody>
                        {selectedQuote.items.map((item, idx) => (
                          <tr key={idx} className="border-t">
                            <td className="px-2 py-1">{item.description}</td>
                            <td className="px-2 py-1 text-right">{item.quantity}</td>
                            <td className="px-2 py-1 text-right">{formatPrice(item.unit_price)}</td>
                            <td className="px-2 py-1 text-right">{formatPriceFCFA(item.unit_price)}</td>
                            <td className="px-2 py-1 text-right">{formatPrice(item.total_ht)}</td>
                            <td className="px-2 py-1 text-right">{formatPriceFCFA(item.total_ht)}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                  
                  <div className="border-t pt-4">
                    {selectedQuote.discount_value > 0 && (
                      <>
                        <div className="flex justify-between mb-2 text-sm">
                          <span>Sous-total HT:</span>
                          <div className="text-right">
                            <div className="font-semibold">{formatPrice(selectedQuote.subtotal_ht || selectedQuote.total_ht)}</div>
                            <div className="text-xs text-gray-500">{formatPriceFCFA(selectedQuote.subtotal_ht || selectedQuote.total_ht)}</div>
                          </div>
                        </div>
                        <div className="flex justify-between mb-2 text-sm text-green-600">
                          <span>Remise ({selectedQuote.discount_value}%):</span>
                          <div className="text-right">
                            <div className="font-semibold">- {formatPrice(selectedQuote.discount_amount || 0)}</div>
                            <div className="text-xs">- {formatPriceFCFA(selectedQuote.discount_amount || 0)}</div>
                          </div>
                        </div>
                      </>
                    )}
                    <div className="flex justify-between mb-2">
                      <span>Total HT:</span>
                      <div className="text-right">
                        <div className="font-semibold">{formatPrice(selectedQuote.total_ht)}</div>
                        <div className="text-xs text-gray-500">{formatPriceFCFA(selectedQuote.total_ht)}</div>
                      </div>
                    </div>
                    <div className="flex justify-between mb-2">
                      <span>TVA ({selectedQuote.tva_rate}%):</span>
                      <div className="text-right">
                        <div className="font-semibold">{formatPrice(selectedQuote.tva_amount)}</div>
                        <div className="text-xs text-gray-500">{formatPriceFCFA(selectedQuote.tva_amount)}</div>
                      </div>
                    </div>
                    <div className="flex justify-between text-lg font-bold border-t pt-2">
                      <span>Total TTC:</span>
                      <div className="text-right">
                        <div>{formatPrice(selectedQuote.total_ttc)}</div>
                        <div className="text-sm text-gray-600">{formatPriceFCFA(selectedQuote.total_ttc)}</div>
                      </div>
                    </div>
                  </div>
                </>
              )}
              
              {selectedQuote.projectType && (
                <div>
                  <h4 className="font-semibold mb-2">Type de projet</h4>
                  <p className="text-sm">{selectedQuote.projectType}</p>
                </div>
              )}
              
              {selectedQuote.message && (
                <div>
                  <h4 className="font-semibold mb-2">Message</h4>
                  <p className="text-sm text-gray-600">{selectedQuote.message}</p>
                </div>
              )}
              
              {selectedQuote.notes && (
                <div>
                  <h4 className="font-semibold mb-2">Notes</h4>
                  <p className="text-sm text-gray-600">{selectedQuote.notes}</p>
                </div>
              )}
              
              {/* Acomptes */}
              {selectedQuote.payments && selectedQuote.payments.length > 0 && (
                <div className="border-t pt-4">
                  <h4 className="font-semibold mb-2">Historique des Acomptes</h4>
                  <table className="w-full text-xs">
                    <thead className="bg-gray-50">
                      <tr>
                        <th className="px-2 py-1 text-left">Date</th>
                        <th className="px-2 py-1 text-left">Méthode</th>
                        <th className="px-2 py-1 text-right">Montant (€)</th>
                        <th className="px-2 py-1 text-right">Montant (FCFA)</th>
                        <th className="px-2 py-1 text-left">Référence</th>
                      </tr>
                    </thead>
                    <tbody>
                      {selectedQuote.payments.map((payment, idx) => (
                        <tr key={idx} className="border-t">
                          <td className="px-2 py-1">{formatDate(payment.date)}</td>
                          <td className="px-2 py-1">{payment.method}</td>
                          <td className="px-2 py-1 text-right font-semibold">{formatPrice(payment.amount)}</td>
                          <td className="px-2 py-1 text-right font-semibold">{formatPriceFCFA(payment.amount)}</td>
                          <td className="px-2 py-1 text-xs text-gray-500">{payment.reference || '-'}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                  <div className="mt-3 p-2 bg-blue-50 rounded">
                    <div className="flex justify-between text-sm">
                      <span>Total payé:</span>
                      <div className="text-right">
                        <div className="font-semibold">{formatPrice(selectedQuote.amount_paid || 0)}</div>
                        <div className="text-xs text-gray-600">{formatPriceFCFA(selectedQuote.amount_paid || 0)}</div>
                      </div>
                    </div>
                    <div className="flex justify-between text-sm mt-1">
                      <span>Restant dû:</span>
                      <div className="text-right">
                        <div className="font-semibold text-orange-600">{formatPrice(selectedQuote.amount_due || 0)}</div>
                        <div className="text-xs text-gray-600">{formatPriceFCFA(selectedQuote.amount_due || 0)}</div>
                      </div>
                    </div>
                  </div>
                </div>
              )}
            </div>
            
            <div className="flex gap-2 mt-6">
              <button
                onClick={() => adminQuotesNewAPI.downloadPDF(selectedQuote.id)}
                className="flex-1 px-4 py-2 bg-purple-600 text-white rounded hover:bg-purple-700"
              >
                📄 Télécharger PDF
              </button>
              <button
                onClick={() => setSelectedQuote(null)}
                className="flex-1 px-4 py-2 bg-gray-600 text-white rounded hover:bg-gray-700"
              >
                Fermer
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Modal Création/Édition */}
      {showCreateModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4" onClick={() => { setShowCreateModal(false); setEditingQuote(null); }}>
          <div className="bg-white rounded-lg p-6 max-w-4xl w-full max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
            <h3 className="text-xl font-bold mb-4">{editingQuote ? `Modifier Devis ${editingQuote.quote_number || editingQuote.id.substring(0, 8)}` : 'Nouveau Devis'}</h3>
            
            <form onSubmit={handleCreateQuote} className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-semibold mb-1">Nom du client *</label>
                  <input
                    type="text"
                    value={formData.name}
                    onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                    className="w-full px-3 py-2 border rounded"
                    required
                  />
                </div>
                
                <div>
                  <label className="block text-sm font-semibold mb-1">Email *</label>
                  <input
                    type="email"
                    value={formData.email}
                    onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                    className="w-full px-3 py-2 border rounded"
                    required
                  />
                </div>
                
                <div>
                  <label className="block text-sm font-semibold mb-1">Téléphone</label>
                  <input
                    type="tel"
                    value={formData.phone}
                    onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                    className="w-full px-3 py-2 border rounded"
                  />
                </div>
                
                <div>
                  <label className="block text-sm font-semibold mb-1">Date d'émission *</label>
                  <input
                    type="date"
                    value={formData.issue_date}
                    onChange={(e) => setFormData({ ...formData, issue_date: e.target.value })}
                    className="w-full px-3 py-2 border rounded"
                    required
                  />
                </div>
              </div>
              
              <div>
                <label className="block text-sm font-semibold mb-1">Adresse complète</label>
                <textarea
                  value={formData.client_address}
                  onChange={(e) => setFormData({ ...formData, client_address: e.target.value })}
                  className="w-full px-3 py-2 border rounded"
                  rows="2"
                />
              </div>
              
              <div>
                <div className="flex items-center justify-between mb-2">
                  <label className="block text-sm font-semibold">Articles</label>
                  <button
                    type="button"
                    onClick={addItem}
                    className="px-3 py-1 bg-green-600 text-white text-sm rounded hover:bg-green-700"
                  >
                    + Ajouter ligne
                  </button>
                </div>
                
                {formData.items.map((item, idx) => (
                  <div key={idx} className="mb-3 p-3 bg-gray-50 rounded">
                    <div className="grid grid-cols-2 gap-2 mb-2">
                      <div className="col-span-2">
                        <label className="block text-xs font-semibold mb-1">Sélectionner un produit ou saisir manuellement</label>
                        <select
                          value={item.product_id}
                          onChange={(e) => handleProductSelect(idx, e.target.value)}
                          className="w-full px-2 py-1 border rounded text-sm"
                        >
                          <option value="">-- Choisir un produit --</option>
                          <option value="manual">✏️ Saisie manuelle</option>
                          {products.map(product => (
                            <option key={product.id} value={product.id}>
                              {product.name} - {product.price}
                            </option>
                          ))}
                        </select>
                      </div>
                    </div>
                    
                    <div className="flex gap-2">
                      <div className="flex-1">
                        <label className="block text-xs font-semibold mb-1">Description *</label>
                        <input
                          type="text"
                          placeholder="Description de l'article"
                          value={item.description}
                          onChange={(e) => handleItemChange(idx, 'description', e.target.value)}
                          className="w-full px-2 py-1 border rounded text-sm"
                          required
                        />
                      </div>
                      <div className="w-24">
                        <label className="block text-xs font-semibold mb-1">Qté *</label>
                        <input
                          type="number"
                          placeholder="Qté"
                          value={item.quantity}
                          onChange={(e) => handleItemChange(idx, 'quantity', parseFloat(e.target.value) || 0)}
                          className="w-full px-2 py-1 border rounded text-sm"
                          step="0.01"
                          min="0"
                          required
                        />
                      </div>
                      <div className="w-32">
                        <label className="block text-xs font-semibold mb-1">Prix HT *</label>
                        <input
                          type="number"
                          placeholder="Prix HT"
                          value={item.unit_price}
                          onChange={(e) => handleItemChange(idx, 'unit_price', parseFloat(e.target.value) || 0)}
                          className="w-full px-2 py-1 border rounded text-sm"
                          step="0.01"
                          min="0"
                          required
                        />
                      </div>
                      <div className="w-32">
                        <label className="block text-xs font-semibold mb-1">Total HT</label>
                        <div className="w-full px-2 py-1 bg-gray-100 rounded text-sm flex items-center justify-end font-semibold">
                          {formatPrice(item.total_ht)}
                        </div>
                      </div>
                      {formData.items.length > 1 && (
                        <div className="flex items-end">
                          <button
                            type="button"
                            onClick={() => removeItem(idx)}
                            className="px-2 py-1 bg-red-600 text-white text-sm rounded hover:bg-red-700"
                          >
                            ×
                          </button>
                        </div>
                      )}
                    </div>
                  </div>
                ))}
              </div>
              
              <div className="grid grid-cols-3 gap-4">
                <div>
                  <label className="block text-sm font-semibold mb-1">Taux TVA (%)</label>
                  <input
                    type="number"
                    step="0.1"
                    min="0"
                    max="100"
                    value={formData.tva_rate}
                    onChange={(e) => setFormData({ ...formData, tva_rate: parseFloat(e.target.value) })}
                    className="w-full px-3 py-2 border rounded"
                    required
                  />
                </div>
                
                <div>
                  <label className="block text-sm font-semibold mb-1">Remise (%)</label>
                  <input
                    type="number"
                    step="0.1"
                    min="0"
                    max="100"
                    value={formData.discount_value}
                    onChange={(e) => {
                      const value = parseFloat(e.target.value) || 0;
                      setFormData({ 
                        ...formData, 
                        discount_value: value,
                        discount_type: value > 0 ? 'percent' : ''
                      });
                    }}
                    className="w-full px-3 py-2 border rounded"
                  />
                  <p className="text-xs text-gray-500 mt-1">Pourcentage de remise</p>
                </div>
                
                <div>
                  <label className="block text-sm font-semibold mb-1">Total HT calculé</label>
                  <div className="w-full px-3 py-2 bg-gray-100 rounded font-semibold">
                    {(() => {
                      const subtotal = formData.items.reduce((sum, item) => sum + item.total_ht, 0);
                      const discount = formData.discount_value > 0 ? subtotal * (formData.discount_value / 100) : 0;
                      const total = subtotal - discount;
                      return formatPrice(total);
                    })()}
                  </div>
                  <p className="text-xs text-gray-500 mt-1">Après remise</p>
                </div>
              </div>
              
              <div>
                <label className="block text-sm font-semibold mb-1">Notes</label>
                <textarea
                  value={formData.notes}
                  onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
                  className="w-full px-3 py-2 border rounded"
                  rows="3"
                />
              </div>
              
              <div className="flex gap-2 pt-4">
                <button
                  type="button"
                  onClick={() => {
                    setShowCreateModal(false);
                    setEditingQuote(null);
                    resetForm();
                  }}
                  className="flex-1 px-4 py-2 bg-gray-200 rounded hover:bg-gray-300"
                >
                  Annuler
                </button>
                <button
                  type="submit"
                  className="flex-1 px-4 py-2 bg-yellow-600 text-black rounded hover:bg-yellow-700"
                >
                  {editingQuote ? 'Enregistrer' : 'Créer Devis'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Modal Acompte */}
      {showPaymentModal && paymentQuote && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4" onClick={() => setShowPaymentModal(false)}>
          <div className="bg-white rounded-lg p-6 max-w-md w-full" onClick={(e) => e.stopPropagation()}>
            <h3 className="text-xl font-bold mb-4">Ajouter un Acompte</h3>
            
            <div className="mb-4 p-3 bg-gray-50 rounded">
              <p className="text-sm"><strong>Devis:</strong> {paymentQuote.quote_number || paymentQuote.id.substring(0, 8)}</p>
              <p className="text-sm"><strong>Client:</strong> {paymentQuote.name}</p>
              <p className="text-sm"><strong>Total TTC:</strong> {formatPrice(paymentQuote.total_ttc || 0)}</p>
              <p className="text-sm"><strong>Déjà payé:</strong> {formatPrice(paymentQuote.amount_paid || 0)}</p>
              <p className="text-sm font-semibold text-orange-600"><strong>Restant dû:</strong> {formatPrice((paymentQuote.total_ttc || 0) - (paymentQuote.amount_paid || 0))}</p>
            </div>
            
            <form onSubmit={handleAddPayment} className="space-y-4">
              <div>
                <label className="block text-sm font-semibold mb-1">Montant de l'acompte (€) *</label>
                <input
                  type="number"
                  step="0.01"
                  min="0.01"
                  max={(paymentQuote.total_ttc || 0) - (paymentQuote.amount_paid || 0)}
                  value={paymentForm.amount}
                  onChange={(e) => setPaymentForm({ ...paymentForm, amount: e.target.value })}
                  className="w-full px-3 py-2 border rounded"
                  required
                />
              </div>
              
              <div>
                <label className="block text-sm font-semibold mb-1">Date de paiement *</label>
                <input
                  type="date"
                  value={paymentForm.date}
                  onChange={(e) => setPaymentForm({ ...paymentForm, date: e.target.value })}
                  className="w-full px-3 py-2 border rounded"
                  required
                />
              </div>
              
              <div>
                <label className="block text-sm font-semibold mb-1">Méthode de paiement *</label>
                <select
                  value={paymentForm.method}
                  onChange={(e) => setPaymentForm({ ...paymentForm, method: e.target.value })}
                  className="w-full px-3 py-2 border rounded"
                  required
                >
                  <option value="bank_transfer">Virement bancaire</option>
                  <option value="cash">Espèces</option>
                  <option value="check">Chèque</option>
                  <option value="paypal">PayPal</option>
                  <option value="manual">Autre</option>
                </select>
              </div>
              
              <div>
                <label className="block text-sm font-semibold mb-1">Référence</label>
                <input
                  type="text"
                  value={paymentForm.reference}
                  onChange={(e) => setPaymentForm({ ...paymentForm, reference: e.target.value })}
                  placeholder="N° de transaction, chèque, etc."
                  className="w-full px-3 py-2 border rounded"
                />
              </div>
              
              <div>
                <label className="block text-sm font-semibold mb-1">Notes</label>
                <textarea
                  value={paymentForm.notes}
                  onChange={(e) => setPaymentForm({ ...paymentForm, notes: e.target.value })}
                  className="w-full px-3 py-2 border rounded"
                  rows="2"
                />
              </div>
              
              <div className="flex gap-2 pt-4">
                <button
                  type="button"
                  onClick={() => setShowPaymentModal(false)}
                  className="flex-1 px-4 py-2 bg-gray-200 rounded hover:bg-gray-300"
                >
                  Annuler
                </button>
                <button
                  type="submit"
                  className="flex-1 px-4 py-2 bg-green-600 text-white rounded hover:bg-green-700"
                >
                  Ajouter Acompte
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

    </div>
  );
}
